import { useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { User, KeyRound, Loader2, ShieldCheck } from "lucide-react";

const ROLE_LABELS = {
  super_admin: "Süper Admin",
  institution_admin: "Kurum Yöneticisi",
  teacher: "Öğretmen",
  parent: "Veli",
};

export default function Profile() {
  const { user, setUser } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [savingProfile, setSavingProfile] = useState(false);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [savingPass, setSavingPass] = useState(false);

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      const { data } = await api.put("/auth/me", { name, phone });
      setUser(data);
      toast.success("Profil bilgileri güncellendi");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSavingProfile(false);
    }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error("Yeni şifreler eşleşmiyor");
      return;
    }
    setSavingPass(true);
    try {
      await api.post("/auth/change-password", {
        current_password: currentPassword,
        new_password: newPassword,
      });
      toast.success("Şifre başarıyla güncellendi");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSavingPass(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-10" data-testid="profile-page">
        <header className="mb-8">
          <p className="text-xs font-semibold uppercase tracking-[0.05em] text-slate-500">Hesap</p>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            Profil Ayarları
          </h1>
          <p className="mt-2 text-sm text-slate-600">Bilgilerini görüntüle ve şifreni güncelle.</p>
        </header>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Profile info */}
          <Card className="rounded-2xl border-slate-200/60 p-8 shadow-[0_2px_8px_rgba(0,0,0,0.04),0_12px_32px_rgba(0,0,0,0.03)]">
            <div className="mb-6 flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-violet-100">
                <User className="h-5 w-5 text-violet-600" />
              </div>
              <div>
                <h2 className="font-heading text-lg font-semibold text-slate-800">Profil Bilgileri</h2>
                <div className="mt-1 flex items-center gap-2">
                  <Badge className="rounded-full bg-violet-100 text-violet-700 hover:bg-violet-100" data-testid="profile-role-badge">
                    <ShieldCheck className="mr-1 h-3 w-3" />
                    {ROLE_LABELS[user?.role] || user?.role}
                  </Badge>
                </div>
              </div>
            </div>

            <form onSubmit={saveProfile} className="space-y-5">
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">E-posta</Label>
                <Input value={user?.email || ""} disabled className="h-11 bg-slate-50 text-slate-500" data-testid="profile-email" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="name" className="text-xs font-semibold uppercase tracking-wider text-slate-500">Ad Soyad</Label>
                <Input id="name" value={name} onChange={(e) => setName(e.target.value)} className="h-11" data-testid="profile-name-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone" className="text-xs font-semibold uppercase tracking-wider text-slate-500">Telefon</Label>
                <Input id="phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Opsiyonel" className="h-11" data-testid="profile-phone-input" />
              </div>
              <Button type="submit" disabled={savingProfile} data-testid="profile-save-button" className="h-11 rounded-xl bg-violet-600 font-semibold text-white hover:bg-violet-700 active:scale-[0.98]">
                {savingProfile ? <Loader2 className="h-4 w-4 animate-spin" /> : "Bilgileri Kaydet"}
              </Button>
            </form>
          </Card>

          {/* Password */}
          <Card className="rounded-2xl border-slate-200/60 p-8 shadow-[0_2px_8px_rgba(0,0,0,0.04),0_12px_32px_rgba(0,0,0,0.03)]">
            <div className="mb-6 flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-100">
                <KeyRound className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <h2 className="font-heading text-lg font-semibold text-slate-800">Şifre Değiştir</h2>
                <p className="text-sm text-slate-500">Güvenliğin için güçlü bir şifre seç.</p>
              </div>
            </div>

            <form onSubmit={savePassword} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="cur" className="text-xs font-semibold uppercase tracking-wider text-slate-500">Mevcut Şifre</Label>
                <Input id="cur" type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} required className="h-11" data-testid="current-password-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="new" className="text-xs font-semibold uppercase tracking-wider text-slate-500">Yeni Şifre</Label>
                <Input id="new" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required className="h-11" data-testid="new-password-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="conf" className="text-xs font-semibold uppercase tracking-wider text-slate-500">Yeni Şifre (Tekrar)</Label>
                <Input id="conf" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required className="h-11" data-testid="confirm-password-input" />
              </div>
              <Button type="submit" disabled={savingPass} data-testid="change-password-button" className="h-11 rounded-xl bg-blue-600 font-semibold text-white hover:bg-blue-700 active:scale-[0.98]">
                {savingPass ? <Loader2 className="h-4 w-4 animate-spin" /> : "Şifreyi Güncelle"}
              </Button>
            </form>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}
